// src/admin/components/apartments/BulkApartmentCreator.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RiAddLine,
  RiDeleteBin6Line,
  RiBuildingLine,
  RiHomeLine,
  RiSaveLine,
  RiEyeLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiInformationLine,
  RiRulerLine,
  RiCloseLine
} from 'react-icons/ri';
import {
  bulkCreateProperties
} from '../../../models/buildingModel';
import {
  getAllApartmentTypes
} from '../../../models/apartmentModel';
import { useTheme } from '../../../contexts/ThemeContext';

const BulkApartmentCreator = ({ buildingId, onSave, onCancel }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();

  const [loading, setLoading] = useState(false);
  const [apartmentTypes, setApartmentTypes] = useState([
    { id: 'Small', name: 'Small' },
    { id: 'Medium', name: 'Medium' },
    { id: 'Large', name: 'Large' },
    { id: 'Extra Large', name: 'Extra Large' },
    { id: 'Cold Storage', name: 'Cold Storage' },
    { id: 'Drive-In', name: 'Drive-In' },
  ]);

  const [floorPlans, setFloorPlans] = useState([
    {
      id: 1,
      floors: [1, 2, 3, 4],
      units: [
        {
          id: 1,
          name: 'Medium',
          quantity: 1,
          type: 'Medium',
          areaSqm: 85,
          isFurnished: false
        },
        {
          id: 2,
          name: 'Small',
          quantity: 1,
          type: 'Small',
          areaSqm: 45,
          isFurnished: false
        }
      ]
    }
  ]);

  const [apartmentNumberFormat, setApartmentNumberFormat] = useState('{floor}{unit}');
  const [previewData, setPreviewData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [floorInputs, setFloorInputs] = useState({});

  // Fetch apartment types
  useEffect(() => {
    const fetchApartmentTypes = async () => {
      try {
        const response = await getAllApartmentTypes();
        if (response && response.types) {
          // Transform types from backend format
          const typesList = response.types.map(type => ({
            id: type.type,
            name: type.type
          }));

          if (typesList.length > 0) {
            setApartmentTypes(typesList);
          }
        }
      } catch (err) {
        console.error('Error fetching apartment types:', err);
      }
    };

    fetchApartmentTypes();
  }, []);

  // Add new floor plan
  const addFloorPlan = () => {
    const newPlan = {
      id: Date.now(),
      floors: [],
      units: []
    };
    setFloorPlans([...floorPlans, newPlan]);
  };

  // Remove floor plan
  const removeFloorPlan = (planId) => {
    setFloorPlans(floorPlans.filter(plan => plan.id !== planId));
  };

  // Update floor plan floors
  const updateFloorPlanFloors = (planId, floors) => {
    setFloorPlans(floorPlans.map(plan =>
      plan.id === planId ? { ...plan, floors } : plan
    ));
  };

  // Add unit type to floor plan
  const addUnitType = (planId) => {
    const newUnit = {
      id: Date.now(),
      name: '',
      quantity: 1,
      type: apartmentTypes[0]?.id || 'Studio',
      areaSqm: 50,
      isFurnished: false
    };

    setFloorPlans(floorPlans.map(plan =>
      plan.id === planId
        ? { ...plan, units: [...plan.units, newUnit] }
        : plan
    ));
  };

  // Remove unit type
  const removeUnitType = (planId, unitId) => {
    setFloorPlans(floorPlans.map(plan =>
      plan.id === planId
        ? { ...plan, units: plan.units.filter(unit => unit.id !== unitId) }
        : plan
    ));
  };

  // Update unit type
  const updateUnitType = (planId, unitId, field, value) => {
    setFloorPlans(floorPlans.map(plan =>
      plan.id === planId
        ? {
          ...plan,
          units: plan.units.map(unit =>
            unit.id === unitId ? { ...unit, [field]: value } : unit
          )
        }
        : plan
    ));
  };

  // Generate preview data
  const generatePreview = () => {
    const preview = [];

    floorPlans.forEach(plan => {
      plan.floors.forEach(floor => {
        let unitNumber = 1;
        plan.units.forEach(unitConfig => {
          for (let i = 0; i < unitConfig.quantity; i++) {
            const formattedUnitNumber = unitNumber.toString().padStart(2, '0');
            const apartmentNumber = apartmentNumberFormat
              .replace('{floor}', floor.toString().padStart(2, '0'))
              .replace('{unit}', formattedUnitNumber);

            preview.push({
              apartmentNumber,
              floor,
              type: unitConfig.name || unitConfig.type,
              areaSqm: unitConfig.areaSqm,
              isFurnished: unitConfig.isFurnished
            });

            unitNumber++;
          }
        });
      });
    });

    setPreviewData(preview);
    setShowPreview(true);
  };

  // Handle save
  // Modify the handleSave function in BulkApartmentCreator.jsx
  const handleSave = async () => {
    if (floorPlans.length === 0) {
      alert(t('add_at_least_one_floor_plan') || 'Please add at least one floor plan');
      return;
    }

    setLoading(true);
    try {
      // Generate the apartments array expected by the backend
      const apartments = [];

      previewData.forEach(item => {
        apartments.push({
          floor: item.floor,
          apartmentNumber: item.apartmentNumber,
          size: item.areaSqm,
          type: item.type,
          status: 'vacant',
          isFurnished: item.isFurnished
        });
      });

      // Check if there are any apartments to create
      if (apartments.length === 0) {
        alert(t('no_apartments_to_create') || 'No apartments to create');
        return;
      }

      // Add console.log to see what's being sent
      console.log('Sending data to API:', { apartments });

      if (buildingId) {
        // Try sending data in the expected format
        await bulkCreateProperties(buildingId, apartments);
      }

      await onSave?.({ apartments });
    } catch (error) {
      console.error('Error creating bulk apartments:', error);
      alert(error.message || 'Error creating bulk apartments');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg overflow-hidden transition-all duration-300`}>
      {/* Header */}
      <div className={`p-5 ${isDarkMode ? 'bg-gray-750 border-b border-gray-700' : 'bg-zinc-50 border-b border-gray-200'}`}>
        <div className="flex justify-between items-center">
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} flex items-center`}>
            <RiBuildingLine className={`mr-3 ${isDarkMode ? 'text-red-400' : 'text-red-500'} text-2xl`} />
            <span>{t('bulk_apartment_creation') || 'Bulk Apartment Creation'}</span>
          </h2>

          <div className="flex space-x-2">
            <button
              onClick={generatePreview}
              className={`px-3 py-2 text-sm rounded-lg ${isDarkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-600 text-white hover:bg-green-700'
                } flex items-center`}
            >
              <RiEyeLine className="mr-2" />
              {t('preview') || 'Preview'} ({previewData.length})
            </button>
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* Apartment Number Format */}
        <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
          <h3 className={`text-md font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex items-center`}>
            <RiInformationLine className="mr-2" /> {t('apartment_number_format') || 'Apartment Number Format'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('format_pattern') || 'Format Pattern'}
              </label>
              <input
                type="text"
                value={apartmentNumberFormat}
                onChange={(e) => setApartmentNumberFormat(e.target.value)}
                className={`w-full p-2.5 text-sm rounded-lg border ${isDarkMode
                    ? 'bg-zinc-800 border-gray-600 text-white focus:ring-red-500 focus:border-red-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:ring-red-500 focus:border-red-500'
                  }`}
                placeholder="{floor}{unit}"
              />
              <p className={`mt-1 text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                {t('format_pattern_help') || 'Use {floor} for floor number and {unit} for unit number. Example: {floor}{unit} = 0201'}
              </p>
            </div>
            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('preview_example') || 'Preview Example'}
              </label>
              <div className={`p-2.5 text-sm rounded-lg border ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'
                }`}>
                {apartmentNumberFormat
                  .replace('{floor}', '02')
                  .replace('{unit}', '01')}
              </div>
            </div>
          </div>
        </div>

        {/* Floor Plans */}
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('floor_plans') || 'Floor Plans'}
            </h3>
            <button
              onClick={addFloorPlan}
              className={`px-4 py-2 text-sm rounded-lg ${isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-600 text-white hover:bg-red-700'
                } flex items-center`}
            >
              <RiAddLine className="mr-2" />
              {t('add_floor_plan') || 'Add Floor Plan'}
            </button>
          </div>

          {floorPlans.map((plan, planIndex) => (
            <div key={plan.id} className={`border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'} rounded-lg p-4`}>
              <div className="flex justify-between items-center mb-4">
                <h4 className={`text-md font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('floor_plan_number', { number: planIndex + 1 }) || `Floor Plan ${planIndex + 1}`}
                </h4>
                <button
                  onClick={() => removeFloorPlan(plan.id)}
                  className={`p-2 ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-800'}`}
                >
                  <RiDeleteBin6Line />
                </button>
              </div>

              {/* Floors Selection */}
              <div className="mb-4">
                <label className={`block mb-2 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('floors_comma_separated') || 'Floors (comma-separated)'}
                </label>
                <input
                  type="text"
                  value={floorInputs[plan.id] || plan.floors.join(', ')}
                  onChange={(e) => {
                    // Store the raw input value
                    setFloorInputs({
                      ...floorInputs,
                      [plan.id]: e.target.value
                    });

                    // Parse the value to get floor numbers
                    const floors = e.target.value
                      .split(',')
                      .map(f => {
                        const parsed = parseInt(f.trim());
                        return !isNaN(parsed) ? parsed : null;
                      })
                      .filter(f => f !== null);

                    updateFloorPlanFloors(plan.id, floors);
                  }}
                  className={`w-full p-2.5 text-sm rounded-lg border ${isDarkMode
                      ? 'bg-zinc-800 border-gray-600 text-white focus:ring-red-500 focus:border-red-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-red-500 focus:border-red-500'
                    }`}
                  placeholder="1, 2, 3, 4"
                />
              </div>

              {/* Unit Types */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('unit_types') || 'Unit Types'}
                  </label>
                  <button
                    onClick={() => addUnitType(plan.id)}
                    className={`px-3 py-1 text-xs rounded ${isDarkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-600 text-white hover:bg-green-700'
                      } flex items-center`}
                  >
                    <RiAddLine className="mr-1" />
                    {t('add_unit_type') || 'Add Unit Type'}
                  </button>
                </div>

                <div className="space-y-3">
                  {plan.units.map((unit, unitIndex) => (
                    <div key={unit.id} className={`p-3 border ${isDarkMode ? 'border-gray-600 bg-gray-750' : 'border-gray-200 bg-gray-50'} rounded`}>
                      <div className="flex justify-between items-center mb-3">
                        <h5 className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {t('unit_type_number', { number: unitIndex + 1 }) || `Unit Type ${unitIndex + 1}`}
                        </h5>
                        <button
                          onClick={() => removeUnitType(plan.id, unit.id)}
                          className={`${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-800'}`}
                        >
                          <RiDeleteBin6Line size={16} />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        <div>
                          <label className={`block mb-1 text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                            {t('name') || 'Name'}
                          </label>
                          <input
                            type="text"
                            value={unit.name}
                            onChange={(e) => updateUnitType(plan.id, unit.id, 'name', e.target.value)}
                            className={`w-full p-2 text-sm rounded border ${isDarkMode
                                ? 'border-gray-600 bg-zinc-800 text-white focus:ring-red-500 focus:border-red-500'
                                : 'border-gray-300 bg-white text-gray-900 focus:ring-red-500 focus:border-red-500'
                              }`}
                            placeholder="Medium"
                          />
                        </div>

                        <div>
                          <label className={`block mb-1 text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                            {t('quantity') || 'Quantity'}
                          </label>
                          <input
                            type="number"
                            value={unit.quantity}
                            onChange={(e) => updateUnitType(plan.id, unit.id, 'quantity', parseInt(e.target.value))}
                            className={`w-full p-2 text-sm rounded border ${isDarkMode
                                ? 'border-gray-600 bg-zinc-800 text-white focus:ring-red-500 focus:border-red-500'
                                : 'border-gray-300 bg-white text-gray-900 focus:ring-red-500 focus:border-red-500'
                              }`}
                            min="1"
                          />
                        </div>

                        <div>
                          <label className={`block mb-1 text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                            {t('type') || 'Type'}
                          </label>
                          <select
                            value={unit.type}
                            onChange={(e) => updateUnitType(plan.id, unit.id, 'type', e.target.value)}
                            className={`w-full p-2 text-sm rounded border ${isDarkMode
                                ? 'border-gray-600 bg-zinc-800 text-white focus:ring-red-500 focus:border-red-500'
                                : 'border-gray-300 bg-white text-gray-900 focus:ring-red-500 focus:border-red-500'
                              }`}
                          >
                            {apartmentTypes.map(type => (
                              <option key={type.id} value={type.id}>
                                {type.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className={`block mb-1 text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                            {t('area_sqm') || 'Area (m²)'}
                          </label>
                          <input
                            type="number"
                            value={unit.areaSqm}
                            onChange={(e) => updateUnitType(plan.id, unit.id, 'areaSqm', parseFloat(e.target.value))}
                            className={`w-full p-2 text-sm rounded border ${isDarkMode
                                ? 'border-gray-600 bg-zinc-800 text-white focus:ring-red-500 focus:border-red-500'
                                : 'border-gray-300 bg-white text-gray-900 focus:ring-red-500 focus:border-red-500'
                              }`}
                            min="0"
                          />
                        </div>
                      </div>

                      <div className="mt-3">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`furnished-${unit.id}`}
                            checked={unit.isFurnished}
                            onChange={(e) => updateUnitType(plan.id, unit.id, 'isFurnished', e.target.checked)}
                            className={`mr-2 h-4 w-4 rounded ${isDarkMode
                                ? 'border-gray-600 bg-zinc-800 text-red-600 focus:ring-red-500'
                                : 'border-gray-300 bg-white text-red-600 focus:ring-red-500'
                              }`}
                          />
                          <label htmlFor={`furnished-${unit.id}`} className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {t('furnished') || 'Furnished'}
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className={`${isDarkMode ? 'bg-zinc-900' : 'bg-white'} rounded-xl shadow-lg max-w-4xl w-full max-h-[80vh] overflow-hidden`}>
              <div className={`p-4 ${isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'} flex justify-between items-center`}>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('apartment_preview_count', { count: previewData.length }) || `Apartment Preview (${previewData.length} units)`}
                </h3>
                <button
                  onClick={() => setShowPreview(false)}
                  className={`${isDarkMode ? 'text-zinc-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <RiCloseLine size={24} />
                </button>
              </div>

              <div className="p-4 overflow-y-auto max-h-96">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {previewData.map((apartment, index) => (
                    <div key={index} className={`p-3 border ${isDarkMode ? 'border-gray-600 bg-gray-750' : 'border-gray-200 bg-gray-50'} rounded`}>
                      <div className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{apartment.apartmentNumber}</div>
                      <div className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                        {t('floor_unit_type', { floor: apartment.floor, type: apartment.type }) || `Floor ${apartment.floor} • ${apartment.type}`}
                      </div>
                      <div className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                        {apartment.areaSqm}m²
                      </div>
                      {apartment.isFurnished && (
                        <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-white text-zinc-900 shadow-sm border border-gray-200 mt-1`}>
                          Furnished
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className={`p-4 ${isDarkMode ? 'border-t border-gray-700' : 'border-t border-gray-200'} flex justify-end space-x-3`}>
                <button
                  onClick={() => setShowPreview(false)}
                  className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                >
                  {t('close') || 'Close'}
                </button>
                <button
                  onClick={handleSave}
                  className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-600 text-white hover:bg-red-700'
                    } flex items-center`}
                  disabled={previewData.length === 0}
                >
                  <RiSaveLine className="mr-2" />
                  {t('create_apartments') || 'Create Apartments'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-8 flex justify-end space-x-4">
          <button
            onClick={onCancel}
            className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isDarkMode
                ? 'bg-zinc-800 text-white hover:bg-gray-600'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              } flex items-center`}
            disabled={loading}
          >
            <RiCloseLine className="mr-1.5" size={18} />
            {t('cancel') || 'Cancel'}
          </button>

          <button
            onClick={handleSave}
            className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isDarkMode
                ? 'bg-red-600 text-white hover:bg-red-700 shadow-md shadow-red-700/20'
                : 'bg-red-600 text-white hover:bg-red-700 shadow-md shadow-red-500/20'
              } flex items-center`}
            disabled={loading || floorPlans.length === 0}
          >
            {loading ? (
              <>
                <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                {t('creating_apartments') || 'Creating Apartments...'}
              </>
            ) : (
              <>
                <RiSaveLine className="mr-1.5" size={18} />
                {t('create_apartments_count', { count: previewData.length }) || `Create ${previewData.length || 0} Apartments`}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkApartmentCreator;